import { AutoSaveIndicator } from '@/components/common/auto-save-indicator';
import { LoadingSpinner } from '@/components/loading/loading-spinner';
import { Button } from '@/components/ui/button';
import { DrawerContainer, DrawerContent, DrawerFooter, DrawerHeader } from '@/components/ui/drawer';
import { useToast } from '@/components/ui/use-toast';
import { useAnalytics } from '@/features/analytics/hooks/use-analytics';
import { CommentsThread } from '@/features/comments/components/comments-thread';
import { DialogForm } from '@/features/dialog/dialog-form';
import { DialogStateManager } from '@/features/dialog/dialog-provider';
import { Badge } from '@/features/list-views/components/badge';
import { useAppState } from '@/lib/hooks/use-app-state';
import { useFormAutoSave } from '@/lib/hooks/use-form-auto-save';
import { useLocale } from '@/lib/hooks/use-locale';
import { CrmTaskStatus, type CrmTaskDTO } from '@/server/dto/crm-task-dto';
import { crmTaskInputDtoSchema, type CrmTaskInputDTO } from '@/server/dto/crm-task-input-dto';
import { api } from '@/utils/api';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckIcon, Trash2Icon } from 'lucide-react';
import { useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useLatest } from 'react-use';
import { useUpdateTask } from '../hooks/use-update-task';
import { DeleteTaskDialog } from './delete-task-dialog';
import { TaskDescriptionField } from './task-description-field';
import { TaskFormFields } from './task-form-fields';

interface TaskDetailProps {
    taskId: string;
    onBack?: () => void;
    onClose?: () => void;
}

interface TaskFormProps {
    task: CrmTaskDTO;
    onBack?: () => void;
    onClose?: () => void;
}

const TaskForm = ({ task, onBack, onClose }: TaskFormProps) => {
    const { t } = useLocale('tasks');
    const appState = useAppState();
    const sendEvent = useAnalytics();
    const { toast } = useToast();
    const apiUtils = api.useUtils();

    const defaultValues = useMemo(() => {
        return {
            dueDate: task?.dueDate ? task?.dueDate : new Date(),
            links: task?.links || [],
            description: task?.descriptionRich,
            assignedToId: task?.assignedTo?.id || appState.platoUser?.id || '',
            status: (task?.status ? task?.status : CrmTaskStatus.OPEN) as CrmTaskStatus,
        } satisfies CrmTaskInputDTO;
    }, [appState.platoUser?.id, task]);

    const form = useForm<CrmTaskInputDTO>({
        defaultValues,
        resolver: zodResolver(crmTaskInputDtoSchema),
    });

    const updateTaskMutation = useUpdateTask({
        onSuccess(data) {
            // Handle task completion analytics
            if (data && typeof data === 'object' && 'status' in data && 'taskId' in data) {
                const taskData = data;

                if (taskData) {
                    if (taskData.status === 'COMPLETED' && task?.status !== taskData.status) {
                        if (taskData.dueDate) {
                            sendEvent('task_completed', {
                                task_id: taskData.taskId,
                                due_date: taskData.dueDate.toISOString(),
                                assignee_id: taskData.assignedTo.id,
                                assignor_id: taskData.assignedBy.id,
                                is_assigned_to_oneself: taskData.assignedTo
                                    ? taskData.assignedTo.id === taskData.assignedBy.id
                                    : false,
                                related_entity_type: taskData.links[0]?.modelType,
                                related_entity_id: taskData.links[0]?.modelId,
                            });
                        }
                    }
                }
            }
        },
        onError(message) {
            toast({
                icon: 'warning',
                title: 'Error',
                description: message || 'An error occurred',
            });
        },
    });

    const {
        watch,
        formState: { isValid, isDirty },
    } = form;

    const formStateRef = useLatest({ isDirty, isValid });

    useFormAutoSave(watch, (values) => {
        if (task && formStateRef.current.isValid && formStateRef.current.isDirty) {
            updateTaskMutation.mutate({
                ...values,
                taskId: task.taskId,
            });
        }
    });

    return (
        <DrawerContainer>
            <DrawerHeader
                onBack={onBack}
                onClose={onClose}
                actions={
                    <>
                        <Button
                            icon={Trash2Icon}
                            variant="icon"
                            size="sm"
                            onClick={() => {
                                if (task) {
                                    DialogStateManager.show(DeleteTaskDialog, {
                                        taskId: task.taskId,
                                        onDeleted: () => {
                                            apiUtils.platoUser.query.tasks.getManyTasks.find.invalidate();
                                            onClose?.();
                                        },
                                    });
                                }
                            }}
                        />
                    </>
                }
                title={t('details__title')}
            >
                <div className="flex flex-row items-center gap-3">
                    <Badge color={task.status === CrmTaskStatus.OPEN ? 'yellow' : 'green'}>
                        {t(`status_${task?.status}`)}
                    </Badge>
                    <AutoSaveIndicator
                        isUpdating={updateTaskMutation.isLoading}
                        isSuccess={updateTaskMutation.isSuccess}
                        isError={updateTaskMutation.isError}
                    />
                </div>
            </DrawerHeader>
            <DrawerContent>
                <DialogForm.ItemList>
                    <div className="rounded-lg bg-white">
                        <TaskDescriptionField form={form} />
                    </div>
                </DialogForm.ItemList>
                <div className="rounded-lg bg-white py-2 px-3 shadow-xs">
                    <TaskFormFields form={form} task={task} />
                </div>
                <CommentsThread taskId={task.taskId} />
            </DrawerContent>
            <DrawerFooter>
                {task?.status === CrmTaskStatus.OPEN ? (
                    <Button
                        icon={CheckIcon}
                        variant="primary"
                        isLoading={updateTaskMutation.isLoading}
                        onClick={() => {
                            if (!task) {
                                return null;
                            }

                            form.setValue('status', CrmTaskStatus.COMPLETED);

                            const values = form.getValues();
                            updateTaskMutation.mutate({
                                ...values,
                                taskId: task.taskId,
                            });
                        }}
                    >
                        {t('form__complete_task')}
                    </Button>
                ) : (
                    <Button
                        variant="subtle"
                        isLoading={updateTaskMutation.isLoading}
                        onClick={() => {
                            if (!task) {
                                return null;
                            }

                            form.setValue('status', CrmTaskStatus.OPEN);
                            const values = form.getValues();

                            updateTaskMutation.mutate({
                                ...values,
                                taskId: task.taskId,
                            });
                        }}
                    >
                        {t('form__open_task')}
                    </Button>
                )}
            </DrawerFooter>
        </DrawerContainer>
    );
};

export const TaskDetails = ({ taskId, onBack, onClose }: TaskDetailProps) => {
    const sendEvent = useAnalytics();
    const { t } = useLocale('tasks');
    const isViewEventSentRef = useRef(false);

    const { data: task, isInitialLoading } = api.platoUser.query.tasks.getManyTasks.find.useQuery({
        taskId: taskId || '',
    });

    useEffect(() => {
        if (task && !isViewEventSentRef.current) {
            isViewEventSentRef.current = true;

            if (task.dueDate) {
                sendEvent('task_viewed', {
                    task_id: task.taskId,
                    due_date: task.dueDate.toISOString(),
                    assignee_id: task.assignedTo?.id,
                    assignor_id: task.assignedBy?.id,
                    is_assigned_to_oneself: task.assignedTo?.id
                        ? task.assignedTo?.id === task.assignedBy?.id
                        : false,
                    related_entity_type: task.links[0]?.modelType,
                    related_entity_id: task.links[0]?.modelId,
                });
            }
        }
    }, [task, sendEvent]);

    if (isInitialLoading) {
        return (
            <div className="w-screen py-20 sm:max-w-2xl xl:max-w-5xl">
                <LoadingSpinner />
            </div>
        );
    }

    if (!task) {
        return (
            <div className="w-screen py-20 sm:max-w-2xl xl:max-w-5xl">
                <div className="text-center">{t('task_not_found')}</div>
            </div>
        );
    }

    return <TaskForm task={task} onBack={onBack} onClose={onClose} />;
};
